import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  IconButton,
  InputAdornment,
  Typography
} from '@mui/material';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { styled } from '@mui/material/styles';
import { APIService } from 'src/api/api';
import InputField from 'src/common/formFields/InputField';
import SelectField from 'src/common/formFields/SelectField';
import ScreenHeader from 'src/common/ScreenHeader';
import useAuth from 'src/context/AuthContext';
import getStaticAssetUrl from 'src/utils/getStaticAssetUrl';
import { USER_ROLES, USER_TYPES } from 'src/utils/constants';
import ROUTE from 'src/utils/routeConstants';
import { loginValidationSchema } from 'src/utils/getInitials';

const BRAND_COLORS = {
  white: '#ffffff',
  navy: '#102036',
  blue: '#3682ae',
  deepBlue: '#004a80'
};

export const LayoutWrapper = styled('div')({
  alignItems: 'center',
  backgroundColor: BRAND_COLORS.navy,
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh'
});

const authFieldSx = {
  '& .MuiInputLabel-root': {
    color: BRAND_COLORS.deepBlue,
    fontSize: 14
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: BRAND_COLORS.white,
    borderRadius: '6px',
    minHeight: 48,
    '& fieldset': {
      borderColor: '#b9d4e5'
    },
    '&:hover fieldset': {
      borderColor: BRAND_COLORS.blue
    },
    '&.Mui-focused fieldset': {
      borderColor: BRAND_COLORS.deepBlue,
      borderWidth: 1
    },
    '&.Mui-error fieldset': {
      borderColor: '#da1e28'
    }
  },
  '& .MuiOutlinedInput-input': {
    color: BRAND_COLORS.navy,
    fontSize: 15,
    lineHeight: 1.5
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0
  }
};

const authSelectSx = {
  ...authFieldSx,
  '& .MuiSelect-select': {
    fontSize: 15,
    paddingBottom: '12.5px',
    paddingTop: '12.5px'
  }
};

const Login = () => {
  const history = useHistory();
  const auth = useAuth();
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const initValues = { email: '', password: '', role: 'admin' };

  const getLoggedInUserRole = (user, values) => {
    let loggedInUserRole = user.role || values.role;
    if (user.role === USER_ROLES.admin) {
      if (values.role !== USER_ROLES.admin) {
        throw new Error('Invalid login');
      }
      loggedInUserRole = USER_ROLES.admin;
    } else if (user.role === USER_ROLES.supervisor) {
      if (values.role !== USER_ROLES.requestor) {
        throw new Error('Invalid login');
      }
      loggedInUserRole = USER_ROLES.requestor;
    } else if (user.role === USER_ROLES.operator) {
      if (values.role !== USER_ROLES.operator) {
        throw new Error('Invalid login');
      }
      loggedInUserRole = USER_ROLES.operator;
    } else if (user.role === USER_ROLES.support) {
      if (values.role !== USER_ROLES.support) {
        throw new Error('Invalid login');
      }
      loggedInUserRole = USER_ROLES.support;
    }
    return loggedInUserRole;
  };

  const getPostLoginRoute = (role) => {
    if (role === USER_ROLES.operator) return ROUTE.cardApprovals;
    if (role === USER_ROLES.support) return ROUTE.support;
    return ROUTE.app;
  };

  const login = async (values) => {
    setLoginError('');
    try {
      const res = await APIService.userLogin(values);
      if (!res.success) throw res;
      if (res.result.token) {
        const userRole = getLoggedInUserRole(res.result, values);
        auth.signin({ ...res.result, role: userRole });
        history.replace(getPostLoginRoute(userRole));
      }
    } catch (error) {
      setLoginError('Invalid credentials, Please try again!');
      console.log('Login error', error.message);
    }
  };

  return (
    <LayoutWrapper>
      <ScreenHeader title="Login" />
      <Box
        sx={{
          boxSizing: 'border-box',
          display: 'flex',
          justifyContent: { xs: 'center', lg: 'flex-end' },
          alignItems: 'center',
          minHeight: '100vh',
          overflow: 'auto',
          px: { xs: 2, sm: 4, lg: 7 },
          py: { xs: 2, md: 4 },
          position: 'relative',
          width: '100%'
        }}
      >
        <Box
          sx={{
            color: BRAND_COLORS.white,
            display: { xs: 'none', lg: 'block' },
            left: 72,
            maxWidth: 560,
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 0,
              mb: 2,
              opacity: 0.9
            }}
          >
            Retrack Fuel Management System
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontSize: { lg: 46, xl: 54 },
              fontWeight: 600,
              letterSpacing: 0,
              lineHeight: 1.08,
              mb: 2
            }}
          >
            Fuel operations, secured from the first click.
          </Typography>
          <Typography
            sx={{
              color: '#d7eaf4',
              fontSize: 16,
              letterSpacing: 0,
              lineHeight: 1.6,
              maxWidth: 500
            }}
          >
            Controlled access for fuel stock, site activity, approvals,
            vehicle consumption and operational reporting.
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.96)',
            border: '1px solid rgba(255, 255, 255, 0.72)',
            borderRadius: '10px',
            boxShadow: '0 30px 80px rgba(16, 32, 54, 0.32)',
            maxWidth: 430,
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            zIndex: 1
          }}
        >
          <Box
            sx={{
              px: { xs: 3, sm: 4 },
              pt: { xs: 3, sm: 3.5 },
              pb: 2.5
            }}
          >
            <Box
              component="img"
              src={getStaticAssetUrl('/static/images/retrack-logo.png')}
              alt="Retrack Automation"
              sx={{
                display: 'block',
                height: { xs: 42, sm: 48 },
                maxWidth: '100%',
                objectFit: 'contain',
                objectPosition: 'left center',
                width: { xs: 190, sm: 220 }
              }}
            />
            <Typography
              sx={{
                color: BRAND_COLORS.navy,
                fontSize: 24,
                fontWeight: 700,
                letterSpacing: 0,
                lineHeight: 1.2,
                mt: 3
              }}
            >
              Sign in to FMS
            </Typography>
            <Typography
              sx={{
                color: BRAND_COLORS.deepBlue,
                fontSize: 14,
                letterSpacing: 0,
                lineHeight: 1.5,
                mt: 0.75
              }}
            >
              Use your assigned role to access the dashboard.
            </Typography>
          </Box>
          <Box sx={{ px: { xs: 3, sm: 4 }, pb: { xs: 3, sm: 3.5 } }}>
            <Formik
              initialValues={initValues}
              validationSchema={loginValidationSchema}
              onSubmit={login}
            >
              {({ handleSubmit, isSubmitting }) => (
                <form onSubmit={handleSubmit}>
                  <InputField
                    name="email"
                    label="Email"
                    type="email"
                    margin="dense"
                    fullWidth
                    disabled={isSubmitting}
                    autoComplete="email"
                    variant="outlined"
                    sx={authFieldSx}
                  />
                  <InputField
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    margin="dense"
                    fullWidth
                    disabled={isSubmitting}
                    autoComplete="current-password"
                    variant="outlined"
                    sx={authFieldSx}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            aria-pressed={showPassword}
                            edge="end"
                            onClick={() => setShowPassword((isVisible) => !isVisible)}
                            disabled={isSubmitting}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <Box sx={{ mt: 2 }}>
                    <SelectField
                      name="role"
                      label="Select Role"
                      value={initValues.role}
                      data={USER_TYPES}
                      fullWidth
                      disabled={isSubmitting}
                      sx={authSelectSx}
                    />
                  </Box>
                  <FormHelperText
                    error
                    sx={{
                      minHeight: 24,
                      ml: 0,
                      mt: 1.25
                    }}
                  >
                    {loginError}
                  </FormHelperText>
                  <Button
                    color="primary"
                    disabled={isSubmitting}
                    fullWidth
                    size="large"
                    type="submit"
                    endIcon={isSubmitting ? null : <LoginRoundedIcon />}
                    sx={{
                      backgroundColor: BRAND_COLORS.deepBlue,
                      borderRadius: '6px',
                      boxShadow: 'none',
                      fontSize: 15,
                      fontWeight: 700,
                      justifyContent: 'space-between',
                      letterSpacing: 0,
                      mt: 0.5,
                      minHeight: 48,
                      px: 2.25,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: BRAND_COLORS.navy,
                        boxShadow: 'none'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#c4d9e7',
                        color: BRAND_COLORS.deepBlue
                      },
                      '& .MuiButton-endIcon': {
                        ml: 'auto'
                      }
                    }}
                    variant="contained"
                  >
                    {isSubmitting ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : (
                      'Continue to dashboard'
                    )}
                  </Button>
                </form>
              )}
            </Formik>
          </Box>
        </Box>
      </Box>
    </LayoutWrapper>
  );
};

export default Login;
